import React, { useState } from 'react';
import Popover from '@mui/material/Popover';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import { Stack, Divider } from '@mui/material';

import { calcDateTime, DateTimeFormat, formatDateTime, isSameDateTime } from '@occc-app/lib/date';

import { DateRangeSelectorButton } from './date-range-selector-button.component';
import { DateRangePicker } from './date-range-picker.component';
import {
  DatePickerRangeValue,
  DateRangePreset,
  OnDatePickerRangeChange,
  RangeTypeWithCustom,
} from './date-range-selector.types';

type DateRangeSelectorProps = {
  value: DatePickerRangeValue;
  onChange: OnDatePickerRangeChange;
  dateRangePresets: DateRangePreset[];
  minSelectableDate?: Date;
  maxSelectableDate?: Date;
  disabled?: boolean;
};

export const DateRangeSelector = ({
  value,
  onChange,
  dateRangePresets,
  minSelectableDate,
  maxSelectableDate,
  disabled = false,
}: DateRangeSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const isCustomRange = !dateRangePresets.some((p) => p.isSelected(value.startDate, value.endDate));

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(true);
    setAnchorEl(event.currentTarget);
    setShowPicker(isCustomRange);
  };
  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const showDateRangePicker = () => setShowPicker(true);
  const hideDateRangePicker = () => setShowPicker(false);

  const onDatesChange = (startDate: Date, endDate: Date, rangeType?: RangeTypeWithCustom) => {
    onChange(
      {
        startDate,
        endDate,
      },
      rangeType || 'custom',
    );

    setOpen(false);
  };

  const renderDateRangeSelectorLabel = () => {
    const { startDate, endDate } = value;
    const preset = dateRangePresets.find((p) => p.isSelected(startDate, endDate));

    if (preset) {
      return preset.label;
    }

    if (isSameDateTime(startDate, endDate, 'day')) {
      return formatDateTime(startDate, DateTimeFormat.Date);
    }

    const sameYear = isSameDateTime(startDate, endDate, 'year');
    const df = sameYear ? DateTimeFormat.Date : DateTimeFormat.DateWithYear;

    return `${formatDateTime(startDate, df)} - ${formatDateTime(endDate, df)}`;
  };

  // Anchor the implicit lower-bound defaults to the upper bound (when provided) so they can
  // never land after it. Without this, a past maxSelectableDate combined with the now-based
  // fallbacks would yield an empty selectable window (minDate > maxDate).
  const upperBoundReference = maxSelectableDate || new Date();

  const effectiveMinSelectableDate =
    minSelectableDate || calcDateTime(upperBoundReference, 'sub', 1, 'year');

  // When a selectable window is provided (e.g. a survey's start/end), open the custom
  // picker on that window: start calendar on the window start, end calendar on the window
  // end (or today when the window has no upper bound). Consumers without a window keep the
  // previous "last month → today" default.
  const defaultStartDate =
    minSelectableDate || calcDateTime(upperBoundReference, 'sub', 1, 'month');
  const defaultEndDate = maxSelectableDate || new Date();

  // Only block future dates when there is no explicit upper bound. A provided
  // maxSelectableDate already caps the range and may legitimately be in the future
  // (e.g. an active survey whose End Date has not arrived yet).
  const disableFuture = !maxSelectableDate;

  return (
    <>
      <DateRangeSelectorButton open={open} handleOpen={handleClick} disabled={disabled}>
        {renderDateRangeSelectorLabel()}
      </DateRangeSelectorButton>
      <Popover
        anchorEl={anchorEl}
        open={open}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onClose={handleClose}
      >
        <Stack direction='row' divider={<Divider orientation='vertical' flexItem />}>
          <DateRangePicker
            show={showPicker}
            startDate={isCustomRange ? value.startDate : defaultStartDate}
            endDate={isCustomRange ? value.endDate : defaultEndDate}
            onDatesChange={onDatesChange}
            onClose={hideDateRangePicker}
            minSelectableDate={effectiveMinSelectableDate}
            maxSelectableDate={maxSelectableDate}
            disableFuture={disableFuture}
          />
          <MenuList>
            {dateRangePresets.map((preset) => (
              <MenuItem
                key={preset.value}
                onClick={() => onDatesChange(preset.startDate, preset.endDate, preset.value)}
                selected={preset.isSelected(value.startDate, value.endDate)}
              >
                {preset.label}
              </MenuItem>
            ))}
            <MenuItem onClick={showDateRangePicker} selected={isCustomRange}>
              Custom
            </MenuItem>
          </MenuList>
        </Stack>
      </Popover>
    </>
  );
};
